import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Grid,
  Chip,
  alpha
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { investorApi } from "../../api/api";
import { useSelector } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import { QRCodeCanvas } from "qrcode.react";
import { AccountBalance, Code, LocationOn } from "@mui/icons-material";
import NumbersIcon from '@mui/icons-material/Numbers';

const VendorSubscriptionPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);

  const { userId, planId, selectedPlan, selectedPeriod, amount } = location.state || {};

  // console.log("Received Data:", { userId, planId, selectedPlan, selectedPeriod, amount, paymentScreenshot });
  // debugger;

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, [step]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await investorApi.getSubscriptionPlans();
        setPlans(response.data);
      } catch (error) {
        console.error("Failed to load subscription plans:", error);
      }
    };
    fetchPlans();
  }, []);

  const selectedPlanData = plans.find(plan => plan.name === selectedPlan);

  const paymentType = "neft";

  const handlePaymentSubmit = async () => {
    if (!paymentScreenshot) {
      alert("Please upload a payment screenshot before submitting.");
      return;
    }

    const payload = new FormData();
    payload.append("userId", userId);
    payload.append("planId", selectedPlanData.id);
    payload.append("period", selectedPeriod);
    payload.append("amount", amount);
    payload.append("payment_type", paymentType);
    payload.append("screenshot", paymentScreenshot);

    try {
      setLoading(true);
      const response = await investorApi.confirmSubscriptionPayment(payload);
      if (response?.status === 200) {
        alert("Subscription Payment Submitted Successfully!");
      } else if(response?.status === 209){
        alert(response?.data?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Payment submission failed", error);
      alert("Failed to submit payment.");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Plan Details", "Bank Transfer", "Upload Proof"];

  const bankDetails = [
    { icon: <AccountBalance />, label: "Bank Name", value: "HDFC Bank" },
    { icon: <NumbersIcon />, label: "Account Number", value: "50100234765981" },
    { icon: <Code />, label: "IFSC Code", value: "HDFC0000123" },
    { icon: <AccountBalance />, label: "Account Holder", value: "Prabhakaran P" },
    { icon: <AccountBalance />, label: "Account Type", value: "Current Account" },
    { icon: <LocationOn />, label: "Branch", value: "Madurai Main Branch" }
  ];

  if (!selectedPlanData) {
    return (
      <Typography sx={{ p: 3, textAlign: "center" }}>
        No subscription plan data available. Please go back and select a plan.
      </Typography>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: 600,
          margin: "auto",
          p: 3,
          mt: 3,
          borderRadius: 2,
          backgroundColor: "#fff",
          boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ position: "absolute", top: 8, right: 8, color: "#666" }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" gutterBottom sx={{ textAlign: "center", mb: 3 }}>
          Subscription Payment
        </Typography>

        <Stepper activeStep={step - 1} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: Plan Details */}
        {step === 1 && (
          <Box>
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#fafafa",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                '&:hover': {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)"
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1a237e",
                        fontSize: "1.25rem",
                        mb: 0.5
                      }}
                    >
                      {selectedPlanData.name} Plan
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontWeight: 500
                      }}
                    >
                      📅 {selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)} Subscription
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: "#e8f5e8",
                      color: "#2e7d32",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: 700
                    }}
                  >
                    ACTIVE
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    mt: 3
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                      PROPERTY LIMIT
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                      {selectedPlanData.property_limit}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                      PROJECT LIMIT
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                      {selectedPlanData.project_limit}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                      BILLING PERIOD
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                      {selectedPeriod}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                      PLAN TYPE
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: "#333" }}>
                      Subscription
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Financial Breakdown */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "white"
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  💰 PAYMENT BREAKDOWN
                </Typography> 

                <Box sx={{ space: 2 }}>
                  {[
                    {
                      label: "Plan Amount",
                      value: `₹${parseFloat(amount).toLocaleString()}`,
                      color: "#1a237e"
                    },
                    {
                      label: "Billing Cycle",
                      value: selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1),
                      color: "#2e7d32"
                    },
                    {
                      label: "Plan Name",
                      value: selectedPlanData.name,
                      color: "#666"
                    }
                  ].map((item, index) => (
                    <Box
                      key={item.label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 2,
                        borderBottom: index < 2 ? "1px solid" : "none",
                        borderColor: "#f0f0f0"
                      }}
                    >
                      <Typography variant="body1" sx={{ color: "#666", fontWeight: 500 }}>
                        {item.label}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 700,
                          color: item.color,
                          fontSize: "1.1rem"
                        }}
                      >
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Plan Description */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#f8f9ff"
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 2
                  }}
                >
                  📋 Plan Description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    lineHeight: 1.6
                  }}
                >
                  {selectedPlanData.description}
                </Typography>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              fullWidth
              onClick={() => setStep(2)}
              sx={{
                py: 1.5,
                fontWeight: 700,
                fontSize: "1rem",
                backgroundColor: "#1a237e",
                borderRadius: 2,
                textTransform: "none",
                boxShadow: "0 2px 8px rgba(26, 35, 126, 0.3)",
                '&:hover': {
                  backgroundColor: "#283593",
                  boxShadow: "0 4px 12px rgba(26, 35, 126, 0.4)"
                }
              }}
            >
              PROCEED TO BANK TRANSFER →
            </Button>
          </Box>
        )}

        {/* Step 2: Bank Transfer Details */}
        {step === 2 && (
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#1a237e",
                fontWeight: 700,
                textAlign: "center",
                mb: 1,
                fontSize: "1.5rem",
                letterSpacing: "-0.5px"
              }}
            >
              BANK TRANSFER DETAILS
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#666",
                textAlign: "center",
                mb: 4,
                fontWeight: 500,
                fontSize: "14px"
              }}
            >
              Transfer the  Subscription Payment using <strong>NEFT or QR Code (UPI)</strong> to the details below.
            </Typography>

            {/* Payment Amount Highlight */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "2px solid #1a237e",
                backgroundColor: "#f8f9ff",
                textAlign: "center"
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                  PAYMENT AMOUNT
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a237e" }}>
                  ₹{parseFloat(amount).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>

            {/* Bank Details Container */}
            <Box
              sx={{
               
                borderRadius: 2,
                // border: "0px solid #e0e0e0",
                backgroundColor: "white",
              }}
            >
              <CardContent sx={{ padding: { xs: 0, sm: "10px" } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 3,
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'start',
                    gap: 1
                  }}
                >
                  🏦 BANK ACCOUNT DETAILS
                </Typography>

                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "16px",
                    padding: "0",
                    border: "1px solid #e8eaf6",
                    boxShadow: "0 4px 20px rgba(26, 35, 126, 0.08)",
                    mb: 3,
                    overflow: "hidden",

                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      color: "white",
                      padding: "10px",
                      display: "flex",
                      alignItems: "center",
                      gap: 2
                    }}
                  >
                    <Box sx={{ fontSize: "1.4rem" }}>🏦</Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: "700", fontSize: "1.1rem", color: "black" }}>
                        Bank Transfer Details
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "0.85rem", color: "black" }}>
                        Complete your payment using these bank details
                      </Typography>
                    </Box>
                  </Box>

                  {/* Bank Details */}
                  <Box sx={{ padding: "8px 0" }}>
                    {bankDetails.map((detail, index) => (
                      <Box
                        key={detail.label}
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "16px 24px",
                          borderBottom: index < bankDetails.length - 1 ? "1px solid #f5f5f5" : "none",
                          transition: "background-color 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#f8f9ff"
                          },
                          width: "100%"
                        }}
                      >

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: { xs: "100%", sm: "50%" } }}>
                          <Box sx={{ color: "#1a237e", fontSize: "1.2rem" }}>
                            {detail.icon}
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#666",
                              fontWeight: "600",
                              fontSize: "0.9rem"
                            }}
                          >
                            {detail.label}
                          </Typography>
                        </Box>


                        <Box
                          sx={{
                            flex: 1,
                            textAlign: { xs: "center", sm: "start" },
                            width: { xs: "100%", sm: "40%" },
                            marginTop: { xs: "10px", sm: "0px" },

                          }}>
                          <Typography
                            variant="body1"
                            sx={{
                              color: "#1a237e",
                              fontWeight: "700",
                              fontSize: "0.95rem",
                              wordBreak: "break-word"
                            }}
                          >
                            {detail.value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Card
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    // backgroundColor: "red",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.08)"
                  }}
                >
                  <CardContent sx={{ p: 1, textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#333",
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // gap: 1
                      }}
                    >
                      📱 ALTERNATIVE: UPI PAYMENT
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-block",
                        p: 3,
                        borderRadius: 2,
                        backgroundColor: "#fff",
                        border: "1px solid #f0f0f0",
                        mb: 3,
                        position: "relative"
                      }}
                    >
                      <QRCodeCanvas
                        value={`upi://pay?pa=prabhakaranplkk@okhdfcbank&pn=${encodeURIComponent(
                          "Prabhakaran"
                        )}&am=${amount}&cu=INR`}
                        size={200}
                        includeMargin={true}
                      />


                      <Box
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          backgroundColor: "#1a237e",
                          color: "white",
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "12px",
                          fontWeight: "bold"
                        }}
                      >
                        ₹
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: 2,
                        p: { xs: 2, sm: 2.5 },
                        border: "1px solid #e9ecef"
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: "#333",
                          mb: 2,
                          textAlign: "center",
                          fontSize: { xs: "0.9rem", sm: "1rem" }
                        }}
                      >
                        UPI PAYMENT INSTRUCTIONS
                      </Typography>

                      {/* Stack layout for mobile, side-by-side for desktop */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: { xs: 1.5, sm: 2 },
                          mb: 2,
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}
                      >
                        <Box sx={{ textAlign: { xs: 'center', sm: 'left' }, flex: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#666",
                              fontWeight: 600,
                              display: 'block',
                              fontSize: { xs: "0.75rem", sm: "0.75rem" }
                            }}
                          >
                            PAYMENT METHOD
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#1a237e",
                              fontSize: { xs: "0.85rem", sm: "0.875rem" }
                            }}
                          >
                            UPI QR Code
                          </Typography>
                        </Box>

                        <Box sx={{ textAlign: { xs: 'center', sm: 'right' }, flex: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#666",
                              fontWeight: 600,
                              display: 'block',
                              fontSize: { xs: "0.75rem", sm: "0.75rem" }
                            }}
                          >
                            UPI ID
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#1a237e",
                              fontSize: { xs: "0.8rem", sm: "0.875rem" },
                              wordBreak: "break-all"
                            }}
                          >
                            prabhakaranplkk@okhdfcbank
                          </Typography>
                        </Box>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          textAlign: "center",
                          fontWeight: 500,
                          backgroundColor: "#e8f5e8",
                          p: { xs: 1, sm: 1.5 },
                          borderRadius: 1,
                          border: "1px solid #c8e6c9",
                          fontSize: { xs: "0.8rem", sm: "0.875rem" }
                        }}
                      >
                        Supported Apps: <strong>GPay • PhonePe • Paytm • BHIM</strong>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Important Instructions */}
                <Box
                  sx={{
                    backgroundColor: "#fff3cd",
                    borderRadius: 2,
                    p: 2.5,
                    border: "1px solid #ffeaa7",
                    mb: 3
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: "#856404",
                      mb: 2,
                      textAlign: "center",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1
                    }}
                  >
                    ⚠️ IMPORTANT TRANSFER INSTRUCTIONS
                  </Typography>

                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {[
                      `Transfer exact amount: ₹${parseFloat(amount).toLocaleString()}`,
                      "Use NEFT transfer method for faster processing",
                      "Include your full name and 'Subscription' in transaction remarks",
                      "Keep transaction receipt/screenshot for verification",
                      "Transactions may take 1-2 hours to reflect in our account",
                      "Send payment confirmation to support@company.com"
                    ].map((instruction, index) => (
                      <Typography
                        key={index}
                        component="li"
                        variant="body2"
                        sx={{ color: "#856404", mb: 1.5, fontSize: "0.85rem", lineHeight: 1.4 }}
                      >
                        {instruction}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                {/* Processing Time Info */}
                {/* <Box
                  sx={{
                    backgroundColor: "#d1ecf1",
                    borderRadius: 2,
                    p: 2,
                    border: "1px solid #bee5eb"
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#0c5460",
                      textAlign: "center",
                      fontWeight: 600
                    }}
                  >
                    ⏰ Processing Time: 1-2 business days after payment verification
                  </Typography>
                </Box> */}
              </CardContent>
            </Box>

            {/* Security Badge */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 3,
                p: 2,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e9ecef"
              }}
            >
              <Box sx={{ color: "#2e7d32", fontSize: "1.2rem" }}>🔒</Box>
              <Typography variant="body2" sx={{ color: "#666", fontWeight: 500 }}>
                <strong>Secure Banking</strong> • Encrypted Transaction Processing
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 },
              mt: { xs: 3, sm: 2 }
            }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setStep(1)}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  fontWeight: 600,
                  borderRadius: 2,
                  border: "2px solid #e0e0e0",
                  color: "#666",
                  textTransform: "none",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  minHeight: { xs: "48px", sm: "auto" },
                  '&:hover': {
                    border: "2px solid #666",
                    backgroundColor: "#f5f5f5"
                  }
                }}
              >
                ← Back to Details
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={() => setStep(3)}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  backgroundColor: "#2e7d32",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "0 2px 8px rgba(46, 125, 50, 0.3)",
                  minHeight: { xs: "48px", sm: "auto" },
                  '&:hover': {
                    backgroundColor: "#1b5e20",
                    boxShadow: "0 4px 12px rgba(46, 125, 50, 0.4)"
                  }
                }}
              >
                I HAVE TRANSFERRED ✓
              </Button>
            </Box>
          </Box>
        )}

        {/* Step 3: Upload Screenshot */}
        {step === 3 && (
          <Box>
            <Typography
              variant="h5"
              sx={{
                color: "#1a237e",
                fontWeight: 700,
                textAlign: "center",
                mb: 1,
                fontSize: "1.5rem",
                letterSpacing: "-0.5px"
              }}
            >
              PAYMENT VERIFICATION
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#666",
                textAlign: "center",
                mb: 4,
                fontWeight: 500,
              }}
            >
              Please upload a clear screenshot of your payment — either the{" "}
              <strong>QR code payment confirmation</strong> or{" "}
              <strong>NEFT transfer receipt</strong> for processing.
            </Typography>


            {/* Upload Section */}
            <Card
              sx={{
                mb: 3,
                borderRadius: 2,
                border: "2px dashed #1a237e",
                backgroundColor: "#f8f9ff",
                textAlign: "center",
                transition: "all 0.3s ease",
                '&:hover': {
                  borderColor: "#283593",
                  backgroundColor: "#f0f2ff"
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ fontSize: "3rem", mb: 2, color: "#1a237e" }}>📄</Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#1a237e",
                    mb: 1
                  }}
                >
                  UPLOAD TRANSFER CONFIRMATION
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#666",
                    mb: 3,
                    maxWidth: "400px",
                    margin: "0 auto"
                  }}
                >
                  Upload screenshot of your NEFT transfer confirmation from your bank
                </Typography>

                <label
                  htmlFor="payment-upload"
                  style={{ display: "inline-block" }}
                >
                  <Button
                    variant="contained"
                    component="span"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontWeight: 700,
                      fontSize: "1rem",
                      backgroundColor: "#1a237e",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "0 2px 8px rgba(26, 35, 126, 0.3)",
                      '&:hover': {
                        backgroundColor: "#283593",
                        boxShadow: "0 4px 12px rgba(26, 35, 126, 0.4)"
                      }
                    }}
                  >
                    📎 SELECT SCREENSHOT
                  </Button>
                </label>

                <input
                  id="payment-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setPaymentScreenshot(file);
                      setPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                />

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: "#666",
                    mt: 2,
                    fontWeight: 500
                  }}
                >
                  Supported formats: JPG, PNG, PDF • Max size: 5MB
                </Typography>
              </CardContent>
            </Card>

            {/* Preview Section */}
            {previewUrl && (
              <Card
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  backgroundColor: "white",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#333",
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      👁️ TRANSACTION PROOF PREVIEW
                    </Typography>

                    <Box
                      sx={{
                        backgroundColor: "#e8f5e8",
                        color: "#2e7d32",
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        fontWeight: 700
                      }}
                    >
                      READY FOR SUBMISSION
                    </Box>
                  </Box>

                  <Box sx={{ textAlign: "center", p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
                    <img
                      src={previewUrl}
                      alt="Bank Transfer Confirmation Preview"
                      style={{
                        width: "100%",
                        maxHeight: 400,
                        objectFit: "contain",
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0"
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: "1px solid #f0f0f0" }}>
                    <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                      FILE: {paymentScreenshot?.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                      SIZE: {(paymentScreenshot?.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Requirements Card */}
            <Card
              sx={{
                mb: 4,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "#fffbf0"
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: "#333",
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  ✅ VERIFICATION REQUIREMENTS
                </Typography>

                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {[
                    "Clear screenshot of NEFT transfer confirmation",
                    "Visible transaction reference number",
                    `Payment amount matching subscription (₹${parseFloat(amount).toLocaleString()})`,
                    "Bank name and account details visible",
                    "Transaction date and time visible",
                    "Sender name and 'Subscription' in transaction remarks"
                  ].map((requirement, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      sx={{ color: "#666", mb: 1, fontWeight: 500 }}
                    >
                      {requirement}
                    </Typography>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Box sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 1.5, sm: 2 }
            }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setStep(2)}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  fontWeight: 600,
                  borderRadius: 2,
                  border: "2px solid #e0e0e0",
                  color: "#666",
                  textTransform: "none",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  minHeight: { xs: "48px", sm: "auto" },
                  '&:hover': {
                    border: "2px solid #666",
                    backgroundColor: "#f5f5f5"
                  }
                }}
              >
                ← Back to Bank Details
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={loading || !paymentScreenshot}
                onClick={handlePaymentSubmit}
                sx={{
                  py: { xs: 1.25, sm: 1.5 },
                  fontWeight: 700,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  backgroundColor: paymentScreenshot ? "#2e7d32" : "#9e9e9e",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: paymentScreenshot ? "0 2px 8px rgba(46, 125, 50, 0.3)" : "none",
                  minHeight: { xs: "48px", sm: "auto" },
                  '&:hover': paymentScreenshot ? {
                    backgroundColor: "#1b5e20",
                    boxShadow: "0 4px 12px rgba(46, 125, 50, 0.4)"
                  } : {},
                  '&.Mui-disabled': {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e"
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} color="inherit" />
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                      PROCESSING SUBMISSION...
                    </Box>
                    <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                      PROCESSING...
                    </Box>
                  </Box>
                ) : (
                  `SUBMIT FOR VERIFICATION ${paymentScreenshot ? '✓' : ''}`
                )}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VendorSubscriptionPayment;